import React from 'react';

import { Bookmark } from '@mui/icons-material';
import { Box, Button, Fab, Popper, Stack } from '@mui/material';

import { AppSettings } from '@/app/AppSettings';
import { LogEntryType } from '@/parsing/LogEntry';

import { LogViewerTableRowModel, LogViewerTableRowType } from './table/LogViewerTableRowModel';
import { LogEntryRowModel } from './table/rows/log-entry/LogEntryRowModel';
import { shouldPassOnClick } from './table/rows/shouldPassOnClick';

type Props = {
  topOffset?: number;
  rightOffset?: number;
  bookmarkedRowModels: LogViewerTableRowModel[];
  settings: AppSettings;

  onOpenChange: (open: boolean) => void;
  onRequestRemoveAll: () => void;
  onRequestScrollToRow: (rowModel: LogViewerTableRowModel) => void;
  onRequestRemoveBookmark: (rowModel: LogViewerTableRowModel) => void;
};

type State = {
  open: boolean;
};

export class BookmarkFab extends React.Component<Props, State> {
  private fabRef = React.createRef<HTMLButtonElement>();

  constructor(props: Props) {
    super(props);

    this.state = {
      open: false,
    };
  }

  componentDidMount() {
    if (this.props.settings.showBookmarks && !this.state.open) {
      this.setState({
        open: true,
      });
    }
  }

  render() {
    return (
      <>
        <Fab
          ref={this.fabRef}
          size="small"
          color="secondary"
          aria-label="bookmarks"
          className="bookmarks-fab"
          style={{ top: this.props.topOffset || 16, right: this.props.rightOffset || 16 }}
          onClick={() => {
            this.click();
          }}
        >
          <Bookmark />
        </Fab>
        <Popper open={this.state.open} anchorEl={this.fabRef.current} placement="bottom-end">
          <Box sx={{ border: 1, p: 1, bgcolor: 'background.paper' }}>
            {this.props.bookmarkedRowModels.length === 0 ? (
              <span className="no-bookmarks">No bookmarks</span>
            ) : (
              <>
                {this.props.bookmarkedRowModels.map((rowModel, index) => (
                  <BookmarkedRow
                    key={index}
                    rowModel={rowModel}
                    onSelect={() => {
                      this.props.onRequestScrollToRow(rowModel);
                    }}
                    onDelete={() => {
                      this.props.onRequestRemoveBookmark(rowModel);
                    }}
                  />
                ))}

                <Stack direction="row-reverse">
                  <Button
                    size="small"
                    color="warning"
                    style={{ height: 20 }}
                    onClick={() => {
                      this.props.onRequestRemoveAll();
                    }}
                  >
                    CLEAR
                  </Button>
                </Stack>
              </>
            )}
          </Box>
        </Popper>
      </>
    );
  }

  private click() {
    this.props.settings.showBookmarks = !this.state.open;

    this.setState({
      open: this.props.settings.showBookmarks,
    });

    this.props.onOpenChange(this.props.settings.showBookmarks);
  }
}

class BookmarkedRow extends React.Component<{
  rowModel: LogViewerTableRowModel;
  onSelect: () => void;
  onDelete: () => void;
}> {
  render() {
    const rowModel = this.props.rowModel;

    let shortContent: any;

    switch (rowModel.data.type) {
      case LogViewerTableRowType.Summary:
        shortContent = 'Summary';
        break;
      case LogViewerTableRowType.FileDivider:
        shortContent = 'File Divider';
        break;
      case LogViewerTableRowType.LogEntry:
        const entryModel = rowModel as LogEntryRowModel;
        shortContent = this.shortContentForLogEntryRowModel(entryModel);
        break;
    }

    return (
      <div
        className="bookmark-fab-row"
        onClick={(e) => {
          if (shouldPassOnClick(e.nativeEvent.composedPath())) {
            this.props.onSelect();
          }
        }}
      >
        <span className="bookmark-fab-row-index">#{rowModel.data.indexBeforeApplyingFilters}</span>

        <span className="bookmark-fab-row-content">{shortContent}</span>

        <span
          className="bookmark-fab-row-delete-button stop-click-propagation"
          onClick={() => {
            this.props.onDelete();
          }}
        >
          ❌
        </span>
      </div>
    );
  }

  private shortContentForLogEntryRowModel(rowModel: LogEntryRowModel): any {
    switch (rowModel.logEntry.entryType) {
      case LogEntryType.NetworkRequest:
        const path1 = rowModel.logEntry.extra.networkRequest?.url?.pathname || '';
        return `Request ${path1}`;
      case LogEntryType.NetworkResponse:
        const path2 = rowModel.logEntry.extra.networkRequest?.url?.pathname || '';
        return `Response ${path2}`;
      case LogEntryType.Generic:
        const maxLength = 25;
        if (rowModel.logEntry.content.length > maxLength) {
          return `${rowModel.logEntry.content.substring(0, maxLength)}...`;
        }
        return rowModel.logEntry.content;
    }
  }
}
